import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from 'contexts/auth_context/AuthContext';
import { languageContext } from 'contexts/language_context/LanguageContext';
import { themeContext } from 'contexts/theme_context/ThemeContext';
import { listVehicleComplaints } from 'services/vehicleComplaintService';

const STATUS_FILTERS = [
    { value: '', labelKey: 'all', fallback: 'All' },
    { value: 'open', labelKey: 'open', fallback: 'Open' },
    { value: 'in_progress', labelKey: 'in_progress', fallback: 'In progress' },
    { value: 'resolved', labelKey: 'resolved', fallback: 'Resolved' },
];

const dateLocales = {
    ar: 'ar-EG-u-nu-arab',
    en: 'en-US',
};

const formatDateTime = (timestamp, language, copy = {}) => {
    if (!timestamp) return copy.notSpecified || 'Not specified';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return copy.notSpecified || 'Not specified';

    return new Intl.DateTimeFormat(dateLocales[language] || dateLocales.en, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const getComplaintKey = (complaint) => complaint?.id ?? complaint?.claim_id ?? complaint?.history?.timeline_at;

const getStatusValue = (complaint) => (
    complaint?.status?.value
    || complaint?.status
    || 'open'
);

const getStatusLabel = (complaint, copy = {}) => {
    const statusValue = getStatusValue(complaint);
    return copy.statusLabels?.[statusValue]
        || complaint?.status?.label
        || statusValue;
};

const getCategoryLabel = (complaint, copy = {}) => {
    const categoryValue = complaint?.category?.value || complaint?.category;
    return copy.categoryLabels?.[categoryValue]
        || complaint?.category?.label
        || categoryValue
        || 'Complaint';
};

const getDescription = (complaint, copy = {}) => (
    complaint?.description
    || complaint?.customer_complaint_description
    || copy.notSpecified
    || 'Not specified'
);

const getTimelineAt = (complaint) => (
    complaint?.history?.timeline_at
    || complaint?.history?.submitted_at
    || complaint?.customer_submission_date
    || complaint?.staff_reported_date
    || null
);

const getSnapshotFields = (complaint, copy = {}) => {
    const history = complaint?.history || {};
    return [
        {
            label: copy.vehicleAtSubmission || 'Vehicle at submission',
            value: history.vehicle_name_at_submission,
        },
        {
            label: copy.productVersion || 'Product version',
            value: history.product_version_at_submission,
        },
    ].filter((field) => field.value);
};

export default function VehicleComplaintHistory({ chassisNumber }) {
    const { readCookie } = useContext(AuthContext);
    const { webtheme } = useContext(themeContext);
    const { weblanguage, jsonfile } = useContext(languageContext);
    const copy = jsonfile?.[weblanguage]?.vehicleComplaintHistory || {};
    const [statusFilter, setStatusFilter] = useState('');
    const [complaints, setComplaints] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let isMounted = true;

        const loadInitialHistory = async () => {
            if (!chassisNumber) return;
            setIsLoading(true);
            setError('');

            try {
                const page = await listVehicleComplaints({
                    chassisNumber,
                    token: readCookie(),
                    status: statusFilter || undefined,
                    limit: 20,
                });
                if (!isMounted) return;
                setComplaints(page.results);
                setNextCursor(page.nextCursor);
            } catch (err) {
                if (!isMounted) return;
                setComplaints([]);
                setNextCursor(null);
                setError(copy.loadError || 'Unable to load complaint history.');
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadInitialHistory();
        return () => {
            isMounted = false;
        };
    }, [chassisNumber, copy.loadError, readCookie, statusFilter]);

    const loadMore = useCallback(async () => {
        if (!chassisNumber || !nextCursor || isLoadingMore) return;

        setIsLoadingMore(true);
        setError('');
        try {
            const page = await listVehicleComplaints({
                chassisNumber,
                token: readCookie(),
                status: statusFilter || undefined,
                limit: 20,
                cursor: nextCursor,
            });
            setComplaints((currentComplaints) => [
                ...currentComplaints,
                ...page.results,
            ]);
            setNextCursor(page.nextCursor);
        } catch (err) {
            setError(copy.loadMoreError || 'Unable to load more complaints.');
        } finally {
            setIsLoadingMore(false);
        }
    }, [chassisNumber, copy.loadMoreError, isLoadingMore, nextCursor, readCookie, statusFilter]);

    return (
        <div className={`vehicle-complaint-history ${webtheme}-theme`} dir={weblanguage === 'ar' ? 'rtl' : 'ltr'}>
            <div className="vehicle-complaint-history-toolbar">
                <div className="vehicle-complaint-history-filters" role="group" aria-label={copy.filtersLabel || 'Complaint status filters'}>
                    {STATUS_FILTERS.map((filter) => (
                        <button
                            key={filter.value || 'all'}
                            type="button"
                            className={`vehicle-complaint-history-filter ${statusFilter === filter.value ? 'active' : ''}`}
                            aria-pressed={statusFilter === filter.value}
                            onClick={() => setStatusFilter(filter.value)}
                            disabled={isLoading}
                        >
                            {copy.statusFilters?.[filter.labelKey] || filter.fallback}
                        </button>
                    ))}
                </div>
            </div>

            {error && <div className="alert alert-danger" role="alert">{error}</div>}

            {isLoading ? (
                <div className="vehicle-complaint-history-state" role="status">{copy.loading || 'Loading complaint history...'}</div>
            ) : complaints.length > 0 ? (
                <>
                    <ol className="vehicle-complaint-history-list">
                        {complaints.map((complaint) => {
                            const snapshotFields = getSnapshotFields(complaint, copy);
                            const statusValue = String(getStatusValue(complaint)).toLowerCase().replace(/_/g, '-');
                            return (
                                <li className="vehicle-complaint-history-item" key={getComplaintKey(complaint)}>
                                    <article className="vehicle-complaint-ticket">
                                        <header className="vehicle-complaint-ticket-header">
                                            <div className="vehicle-complaint-history-chips">
                                                <span className={`vehicle-complaint-history-chip status status-${statusValue}`}>
                                                    {getStatusLabel(complaint, copy)}
                                                </span>
                                                <span className="vehicle-complaint-history-chip">
                                                    {getCategoryLabel(complaint, copy)}
                                                </span>
                                            </div>
                                            <time className="vehicle-complaint-ticket-date" dateTime={getTimelineAt(complaint) || undefined}>
                                                {formatDateTime(getTimelineAt(complaint), weblanguage, copy)}
                                            </time>
                                        </header>
                                        <p className="vehicle-complaint-ticket-description">
                                            {getDescription(complaint, copy)}
                                        </p>
                                        {snapshotFields.length > 0 && (
                                            <dl className="vehicle-complaint-history-snapshot">
                                                {snapshotFields.map((field) => (
                                                    <div key={field.label}>
                                                        <dt>{field.label}</dt>
                                                        <dd>{field.value}</dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        )}
                                    </article>
                                </li>
                            );
                        })}
                    </ol>
                    {nextCursor && (
                        <div className="vehicle-complaint-history-more">
                            <button
                                type="button"
                                className="vehicle-complaint-history-more-button"
                                onClick={loadMore}
                                disabled={isLoadingMore}
                            >
                                {isLoadingMore ? copy.loadingMore || 'Loading...' : copy.loadMore || 'Load more'}
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div className="vehicle-complaint-history-state">
                    {copy.empty || 'No complaints reported for this vehicle.'}
                </div>
            )}
        </div>
    );
}
